import { useCallback, useEffect, useState } from "react";
import { Coins, Target, TrendingUp, ShoppingBag, CalendarDays, AlertTriangle, Info } from "lucide-react";
import { api, idr, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO } from "@/lib/tanggal";

/** Rincian satu sumber poin (HP terjual, ACC terjual, dst) — kartu kecil,
 *  cuma dipakai di halaman ini. */
function SumberPoin({ label, n, poin }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2.5">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-800 mt-0.5">
        {fmtNum(n)} <span className="text-slate-400 font-normal">→ {fmtNum(poin)} poin</span>
      </p>
    </div>
  );
}

/** Poin & Target Saya — Portal Karyawan (brief owner 21 Agu 2026 poin 2).
 *
 * Endpoint `GET /my/poin` SENGAJA tidak menerima parameter identitas apa pun
 * (tidak ada `emp_id`/`employee_id` di query) — backend selalu mengunci ke
 * karyawan yang tertaut ke akun yang sedang login. Halaman ini karena itu
 * TIDAK PERNAH bisa dipakai untuk melihat poin/insentif rekan kerja, apa pun
 * yang diutak-atik di alamat/parameter — beda dengan halaman admin
 * "Target vs Realisasi" (`/sales-targets`, kunci `sales_targets`) yang memang
 * dirancang untuk melihat seluruh tim.
 */
export default function MyPoin() {
  const { user } = useAuth();
  const [periode, setPeriode] = useState(() => bulanIniISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/my/poin?periode=${periode}`)
      .then(r => setData(r.data))
      .catch(e => { setData(null); setError(e); })
      .finally(() => setLoading(false));
  }, [periode]);

  useEffect(() => { load(); }, [load]);

  const insentif = data?.insentif_rincian || null;

  return (
    <div className="p-4 md:p-8 max-w-[1200px]" data-testid="my-poin-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Portal Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Coins className="w-6 h-6 md:w-7 md:h-7 text-emerald-600" /> Poin &amp; Target Saya
          </h1>
          <p className="mt-1.5 text-xs md:text-sm text-slate-500">
            Selamat datang, {user?.name}. Perolehan poin &amp; pencapaian target periode {periode}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <MonthPicker value={periode} onChange={setPeriode} data-testid="my-poin-periode" />
        </div>
      </header>

      <LoadingState
        loading={loading}
        error={error}
        onRetry={load}
        varian="kartu"
        jumlahKartu={4}
        data-testid="my-poin-loading"
      >
        {data && (
          <>
            {!data.target_diatur && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5"
                data-testid="my-poin-target-belum-diatur">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Target poin Anda untuk periode ini <strong>belum diatur</strong> — hubungi atasan/HR.
                  Poin yang sudah Anda kumpulkan tetap tercatat dan tampil di bawah, tapi persentase
                  pencapaian belum berarti apa-apa selama targetnya kosong.
                </p>
              </div>
            )}

            {/* Ringkasan target vs realisasi */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Target Poin</p>
                <p className="font-display text-2xl font-bold text-slate-900" data-testid="my-poin-target">
                  {data.target_diatur ? fmtNum(data.target_poin) : "—"}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Poin Terkumpul</p>
                <p className="font-display text-2xl font-bold text-emerald-700" data-testid="my-poin-realisasi">
                  {fmtNum(data.realisasi_poin)}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Pencapaian</p>
                <p className="font-display text-2xl font-bold text-slate-900" data-testid="my-poin-pct">
                  {data.target_diatur ? `${data.realisasi_pct}%` : "—"}
                </p>
                {data.target_diatur && (
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
                    <div className={`h-full transition-all ${data.realisasi_pct >= 100 ? "bg-emerald-500" : data.realisasi_pct >= 80 ? "bg-emerald-400" : data.realisasi_pct >= 50 ? "bg-amber-400" : "bg-rose-400"}`}
                      style={{ width: `${Math.min(100, data.realisasi_pct)}%` }} />
                  </div>
                )}
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Omzet Saya</p>
                <p className="font-display text-2xl font-bold text-slate-900" data-testid="my-poin-omzet">
                  {idr(data.omzet)}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {fmtNum(data.n_transaksi_sales)} penjualan · {fmtNum(data.n_transaksi_acq)} akuisisi
                </p>
              </div>
            </div>

            {/* Estimasi insentif rupiah — keputusan owner 21 Agu 2026: tampilkan,
                TAPI berlabel tegas "perkiraan, belum final". */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Estimasi Insentif</h3>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800"
                  data-testid="my-poin-insentif-badge-belum-final">
                  Perkiraan · BELUM FINAL
                </span>
              </div>
              <div className="p-4">
                {!insentif?.tersedia ? (
                  <div className="flex items-start gap-2 text-sm text-slate-500" data-testid="my-poin-insentif-belum-tersedia">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                      Bobot insentif per poin dan/atau target poin Anda belum diatur, jadi estimasi rupiah
                      belum bisa dihitung. Hubungi atasan/HR kalau ini seharusnya sudah ada.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg px-3 py-2.5">
                        <p className="text-[11px] text-slate-500">Insentif Dasar (target × bobot)</p>
                        <p className="font-mono font-bold text-slate-900 mt-0.5">{idr(insentif.insentif_dasar)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg px-3 py-2.5" data-testid="my-poin-insentif-rupiah">
                        <p className="text-[11px] text-emerald-600">Estimasi Cair ({insentif.tier_cair_pct}%)</p>
                        <p className="font-mono font-bold text-emerald-700 mt-0.5">{idr(insentif.insentif_cair)}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      Bobot {idr(data.bobot_per_poin)}/poin · {insentif.pct_realisasi}% dari target.
                    </p>
                  </>
                )}
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3">
                  {insentif?.catatan || "Perkiraan berdasarkan data hari ini — belum final."}
                </p>
              </div>
            </div>

            {/* Rincian sumber poin */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-emerald-600" />
                <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Rincian Sumber Poin</h3>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <SumberPoin label="HP Terjual" n={data.breakdown?.sale_hp?.n || 0} poin={data.breakdown?.sale_hp?.poin || 0} />
                <SumberPoin label="Aksesoris Terjual" n={data.breakdown?.sale_acc?.n || 0} poin={data.breakdown?.sale_acc?.poin || 0} />
                <SumberPoin label="Akuisisi HP" n={data.breakdown?.acq_hp?.n || 0} poin={data.breakdown?.acq_hp?.poin || 0} />
                <SumberPoin label="Akuisisi Device" n={data.breakdown?.acq_device?.n || 0} poin={data.breakdown?.acq_device?.poin || 0} />
              </div>
            </div>
          </>
        )}
      </LoadingState>
    </div>
  );
}
